import { useState } from 'react';
import { restaurantsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Save, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/format';

const emptyRow = () => ({ id: crypto.randomUUID(), old_system: '', mayo_module: '', old_price: 0, new_price: 0 });

export default function RestaurantSystems({ restaurantId, restaurantName = 'restaurant', systems = [], modules = [], hasModules = false, canEdit, onSaved }) {
  const [rows, setRows] = useState(() => systems.map(s => ({ ...s, id: s.id || crypto.randomUUID() })));
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const totalOld = rows.reduce((s, r) => s + (parseFloat(r.old_price) || 0), 0);
  const totalNew = rows.reduce((s, r) => s + (parseFloat(r.new_price) || 0), 0);
  const diff = totalOld - totalNew;

  const updateRow = (id, field, value) => setRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  const addRow = () => setRows(prev => [...prev, emptyRow()]);
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const cleanRows = () => rows
    .filter(r => (r.old_system || '').trim() || (r.mayo_module || '').trim())
    .map(r => ({
      id: r.id,
      old_system: (r.old_system || '').trim(),
      mayo_module: (r.mayo_module || '').trim(),
      old_price: parseFloat(r.old_price) || 0,
      new_price: parseFloat(r.new_price) || 0,
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await restaurantsAPI.update(restaurantId, { systems: cleanRows() });
      toast.success('Systems comparison saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Persist any unsaved edits first so the PDF always matches what is on screen.
      if (canEdit) {
        await restaurantsAPI.update(restaurantId, { systems: cleanRows() });
      }
      const res = await restaurantsAPI.exportProposal(restaurantId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${restaurantName} - Mayo cost comparison.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data instanceof Blob ? 'Add at least one system row or module first' : err.response?.data?.detail;
      toast.error(detail || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden" data-testid="restaurant-systems">
      <div className="p-4 border-b border-[#E0E0E0] flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-[#111111]">Current systems vs Mayo</h3>
          <p className="text-xs text-[#666666]">Monthly cost of the tools the restaurant uses today</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || (rows.length === 0 && !hasModules)}
            title={rows.length === 0 && !hasModules ? 'Add a system row or a module first' : 'Download a branded PDF for the client'}
            data-testid="export-proposal-btn"
          >
            <FileDown size={14} className="mr-1" /> {exporting ? 'Preparing...' : 'Export PDF'}
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="add-system-row-btn">
                <Plus size={14} className="mr-1" /> Add row
              </Button>
              <Button size="sm" className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" onClick={handleSave} disabled={saving} data-testid="save-systems-btn">
                <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="p-6 text-center text-[#666666] text-sm" data-testid="systems-empty">
          No systems recorded yet.{canEdit ? ' Use "Add row" to start the comparison.' : ''}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]" data-testid="systems-table">
            <thead className="bg-[#F5F5F5]">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Old system</th>
                <th className="text-left p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Mayo module</th>
                <th className="text-right p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Old price</th>
                <th className="text-right p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">New price</th>
                {canEdit && <th className="p-3 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[#E0E0E0]" data-testid={`system-row-${r.id}`}>
                  <td className="p-2">
                    {canEdit ? (
                      <Input value={r.old_system || ''} onChange={e => updateRow(r.id, 'old_system', e.target.value)} placeholder="e.g. OpenTable" className="h-9" data-testid={`system-old-name-${r.id}`} />
                    ) : (
                      <span className="text-sm text-[#111111]">{r.old_system || '—'}</span>
                    )}
                  </td>
                  <td className="p-2">
                    {canEdit ? (
                      <>
                        <Input
                          value={r.mayo_module || ''}
                          onChange={e => updateRow(r.id, 'mayo_module', e.target.value)}
                          placeholder="Mayo module or custom text"
                          list={`modules-list-${r.id}`}
                          className="h-9"
                          data-testid={`system-mayo-module-${r.id}`}
                        />
                        <datalist id={`modules-list-${r.id}`}>
                          {modules.map(m => <option key={m.id} value={m.name} />)}
                        </datalist>
                      </>
                    ) : (
                      <span className="text-sm text-[#111111]">{r.mayo_module || '—'}</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {canEdit ? (
                      <Input type="number" value={r.old_price ?? 0} onChange={e => updateRow(r.id, 'old_price', e.target.value)} className="h-9 text-right" data-testid={`system-old-price-${r.id}`} />
                    ) : (
                      <span className="text-sm font-medium">{formatCurrency(r.old_price)}</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {canEdit ? (
                      <Input type="number" value={r.new_price ?? 0} onChange={e => updateRow(r.id, 'new_price', e.target.value)} className="h-9 text-right" data-testid={`system-new-price-${r.id}`} />
                    ) : (
                      <span className="text-sm font-medium">{formatCurrency(r.new_price)}</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="p-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeRow(r.id)} data-testid={`remove-system-${r.id}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#FAFAFA]">
              <tr className="border-t-2 border-[#E0E0E0]">
                <td className="p-3 font-bold text-sm" colSpan={2}>Total per month</td>
                <td className="p-3 text-right font-bold text-sm" data-testid="systems-total-old">{formatCurrency(totalOld)}</td>
                <td className="p-3 text-right font-bold text-sm text-[#059669]" data-testid="systems-total-new">{formatCurrency(totalNew)}</td>
                {canEdit && <td></td>}
              </tr>
              <tr>
                <td className="p-3 text-sm text-[#666666]" colSpan={2}>
                  {diff >= 0 ? 'Monthly savings with Mayo' : 'Extra monthly cost with Mayo'}
                </td>
                <td className="p-3 text-right text-sm text-[#666666]">yearly</td>
                <td className="p-3 text-right" colSpan={canEdit ? 2 : 1}>
                  <span className={`font-bold text-sm ${diff >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`} data-testid="systems-savings">
                    {formatCurrency(Math.abs(diff))}/mo · {formatCurrency(Math.abs(diff) * 12)}/yr
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
